import React, { useEffect, useState, useRef, useCallback } from 'react';
import { useSelector } from 'react-redux';
import { useParams, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import moment from 'moment';

import { Button } from 'components/Button/RoundButton';
import ProgressBar from 'components/ProgressBar/ProgressBar';
import QuestionAndOptionsComponent from './QuestionAndOptions';
import YesNoModal from 'components/Modals/yesNoModal';
import InformationModal from 'components/Modals/informationModal';
import ReportQuestionModal from 'components/Modals/reportQuestion';
import * as actions from './actions';
import { useActions } from 'hooks/useActions';
import {
  TestContainer,
  TestName,
  QuestionAndOptionsContainer,
  TimerAndQuestionsContainer,
  QuestionComponent,
  QuestionsBoxContainer,
  QuestionsSelectionButton,
  TimerContainer,
  TimerTextLabel,
  QuestionsSelectionContainer,
  TopHeadingContainer,
  SubmitAndInfoButtonContainer,
  SubmissionContainer,
  SubmissionText,
  SubmitTestLink,
  ItalicText,
} from './styles';

export default function MultichoiceTestPage() {
  let screenWidth = window.innerWidth;
  let isSmallDevice = screenWidth < 992;

  const { ticketId } = useParams();
  const navigate = useNavigate();

  const [isAdaptiveTest, setIsAdaptiveTest] = useState(true);
  const [timeDonePercentage, setTimeDonePercentage] = useState(0);
  const [timeLeftText, setTimeLeftText] = useState('Calculating...');
  const [showConfirmationModal, setShowConfirmationModal] = useState(false);
  const [showInformationModal, setShowInformationModal] = useState(false);
  const [modalInfo, setModalInfo] = useState({});
  const [showReportQuestionModal, setShowReportQuestionModal] = useState(false);

  const autoSubmitIntervalIdRef = useRef(null);

  const {
    loadTicket,
    closeTicket,
    reportQuestion,
    setCurrentQuestionObject,
    updateInput,
    setCurrentOptionId,
    setCurrentTextAnswer,
  } = useActions(actions);

  const {
    ticketResponse: { ticket, test },
    closedTicket,
    error,
    reportedQuestion,
    questionsAttempted,
    currentQuestionObject,
    currentOptionId,
    nonAdaptiveQuestionObject,
    textAnswer,
  } = useSelector((state) => state.MultichoiceTestPageReducer);

  useEffect(() => {
    loadTicket(ticketId);
  }, [loadTicket, ticketId]);

  const submitTicket = useCallback(
    (isConfirmed) => {
      setShowConfirmationModal(false);
      if (isConfirmed) {
        let params = {
          question: currentQuestionObject?.question?._id || '',
          option: currentOptionId,
          textAnswer: textAnswer ?? '',
        };
        closeTicket(ticketId, params);
      }
    },
    [closeTicket, currentOptionId, currentQuestionObject, ticketId, textAnswer]
  );

  useEffect(() => {
    if (!ticket?.deadlineAt) return;
    const updateTimeLeft = () => {
      let newTimeLeft = moment(ticket.deadlineAt).diff(Date.now(), 'seconds') + ticket.diffToServerTimeInSeconds;
      let newTimeLeftUnit = 'seconds';

      let secondsPassed = moment(Date.now()).diff(ticket.startedAt, 'seconds') - ticket.diffToServerTimeInSeconds;
      setTimeDonePercentage((100 * secondsPassed) / (secondsPassed + newTimeLeft));

      if (newTimeLeft > 60) {
        newTimeLeft = moment(ticket.deadlineAt).add(1, 'minutes').diff(Date.now(), 'minutes');
        newTimeLeft += Math.floor(ticket.diffToServerTimeInSeconds / 60);
        newTimeLeftUnit = 'minutes';
      }

      setTimeLeftText(`${newTimeLeft} ${newTimeLeftUnit}`);

      if (newTimeLeft <= 0) {
        clearInterval(autoSubmitIntervalIdRef.current);
        submitTicket(true);
      }
    };

    updateTimeLeft();
    clearInterval(autoSubmitIntervalIdRef.current);
    autoSubmitIntervalIdRef.current = setInterval(updateTimeLeft, 1000);
    return () => {
      clearInterval(autoSubmitIntervalIdRef.current);
    };
  }, [submitTicket, ticket.deadlineAt, ticket.startedAt, ticket.diffToServerTimeInSeconds]);

  useEffect(() => {
    if (!ticket || !test) return;
    if (ticket.submitted || ticket.archived) navigate('/tickets');
    setIsAdaptiveTest(test.adaptiveTest);
  }, [test, ticket, navigate]);

  useEffect(() => {
    if (error) toast.error(error);
    if (error === 'The ticket has already been submitted') navigate('/tickets');
  }, [error, navigate]);

  useEffect(() => {
    if (!ticketId || closedTicket._id === ticketId) navigate('/tickets');
  }, [closedTicket, ticketId, navigate]);

  const reportQuestionFeedback = (data) => {
    setShowReportQuestionModal(false);
    if (data.isSubmitted) {
      delete data.isSubmitted;
      data.test = test._id;
      data.question = currentQuestionObject.question._id;
      reportQuestion(data);
    }
  };

  useEffect(() => {
    if (reportedQuestion._id) {
      toast.success('Feedback submitted successfully');
    }
  }, [reportedQuestion]);

  const getQuestionsDonePercentage = () => {
    return (questionsAttempted / ticket.totalQuestions) * 100;
  };

  const getProgressBarType = () => {
    if (getQuestionsDonePercentage() - timeDonePercentage >= 0) {
      return 'success';
    } else if (timeDonePercentage - getQuestionsDonePercentage() < 10) {
      return 'warning';
    } else {
      return 'danger';
    }
  };

  const confirmSubmit = () => {
    let message = 'Would you like to submit the test?';

    const unansweredQuestionNumbers = Object.keys(nonAdaptiveQuestionObject)
      .map((key) => {
        return localStorage.getItem(`fsFreeText-${key}`) !== null;
      })
      .reduce((acc, curr, index) => {
        // +1 to get question number
        // Dont warn about current question as it will get saved on submit
        if (curr === true && currentQuestionObject?.questionNumber !== index + 1) acc.push(index + 1);
        return acc;
      }, []);
    const questionsRemainingText =
      'Warning: It looks like you have unsaved changes to question(s) ' + unansweredQuestionNumbers.join(', ') + '. \n';

    let questionsAnswered = isAdaptiveTest ? questionsAttempted + (currentOptionId ? 1 : 0) : questionsAttempted;
    if (questionsAnswered < ticket.totalQuestions) {
      message = `It ${
        unansweredQuestionNumbers.length > 0 ? 'also' : ''
      } looks like you have not answered all of the questions. Would you like to submit your test anyway?`;
    } else if (!isAdaptiveTest) {
      message = `You still have ${timeLeftText} remaining to revise your answers. If you submit the test, then you will not be allowed to make any changes. Would you like to submit your test anyway?`;
    }

    if (unansweredQuestionNumbers.length > 0) {
      message = questionsRemainingText + message;
    }
    setModalInfo({
      title: 'Confirmation',
      body: message,
    });
    setShowConfirmationModal(true);
  };

  const updateTextAnswer = async () => {
    let input = {
      question: currentQuestionObject.question._id,
      textAnswer: textAnswer,
    };
    await updateInput(ticketId, input, isAdaptiveTest);
    localStorage.removeItem(`fsFreeText-${currentQuestionObject.question._id}`); // probably not handling errors from backend. To be checked.};
  };

  const changeQuestion = (value) => {
    if (textAnswer) {
      updateTextAnswer();
    }
    setCurrentOptionId('');
    setCurrentTextAnswer('');
    setCurrentQuestionObject(value);
  };

  const getQuestionsSelectionBox = () => (
    <QuestionsSelectionContainer>
      {Object.values(nonAdaptiveQuestionObject).map((value, index) => {
        return (
          <QuestionsSelectionButton
            key={value.question._id}
            isAnswered={value.question.isAnswered}
            isActive={
              currentQuestionObject &&
              currentQuestionObject.question &&
              value.question._id === currentQuestionObject.question._id
            }
            onClick={() => {
              changeQuestion(value);
            }}
          >
            {index + 1}
          </QuestionsSelectionButton>
        );
      })}
    </QuestionsSelectionContainer>
  );

  const displayTimerSection = () => {
    return (
      <TimerContainer>
        <TimerTextLabel isAdaptiveTest={isAdaptiveTest || isSmallDevice}>
          <ItalicText>{questionsAttempted} </ItalicText> /<ItalicText>{ticket.totalQuestions}</ItalicText> answered
        </TimerTextLabel>
        <ProgressBar isMultichoice percent={getQuestionsDonePercentage()} barType="track" />
        <TimerTextLabel isAdaptiveTest={isAdaptiveTest || isSmallDevice}>
          {timeLeftText ? `${timeLeftText} left of ${ticket.timeLimit}` : 'Calculating...'}
        </TimerTextLabel>
        <ProgressBar isMultichoice percent={timeDonePercentage} barType={getProgressBarType()} />
      </TimerContainer>
    );
  };

  return (
    <TestContainer>
      {showConfirmationModal && (
        <YesNoModal modalInfo={modalInfo} confirm={(isConfirmed) => submitTicket(isConfirmed)} />
      )}
      {showInformationModal && (
        <InformationModal
          title={test.name}
          textHTML={test.candidateDescription}
          closeModal={() => setShowInformationModal(false)}
        />
      )}
      {showReportQuestionModal && (
        <ReportQuestionModal modalInfo={modalInfo} confirm={(data) => reportQuestionFeedback(data)} />
      )}

      <TopHeadingContainer>
        <TestName>{test.name}</TestName>
        <Button primaryBlue primaryGreenBorder removeMargin onClick={() => setShowReportQuestionModal(true)}>
          <i className="fa fa-flag">&nbsp;</i> {screenWidth > 600 ? 'Report question' : ''}
        </Button>
      </TopHeadingContainer>

      {(isAdaptiveTest || isSmallDevice) && displayTimerSection()}
      {ticket?._id && (
        <QuestionAndOptionsContainer>
          <QuestionComponent isAdaptiveTest={isAdaptiveTest}>
            {!isAdaptiveTest && !currentQuestionObject ? (
              <SubmissionContainer>
                <SubmissionText>
                  Thank you for answering the questions. Feel free to change your answers until the deadline has passed.
                  <br />
                  <SubmitTestLink onClick={() => confirmSubmit()}>Submit Test</SubmitTestLink>
                </SubmissionText>
              </SubmissionContainer>
            ) : (
              <QuestionAndOptionsComponent />
            )}
          </QuestionComponent>
          {isAdaptiveTest && (
            <SubmitAndInfoButtonContainer isAdaptiveTest>
              <Button
                title="Show description"
                removeMargin
                primaryBlue
                primaryGreenBorder
                onClick={() => setShowInformationModal(true)}
              >
                <i className="fa fa-info">&nbsp;</i>Info
              </Button>
              <Button
                removeMargin
                primaryBlue
                primaryGreenBorder
                title="Submit this test"
                onClick={() => confirmSubmit()}
              >
                <i className="fa-regular fa-paper-plane">&nbsp;</i>Submit
              </Button>
            </SubmitAndInfoButtonContainer>
          )}
          {!isAdaptiveTest && (
            <TimerAndQuestionsContainer>
              <QuestionsBoxContainer>
                {!isSmallDevice && displayTimerSection()}
                {getQuestionsSelectionBox()}
                <SubmitAndInfoButtonContainer>
                  <Button title="Show description" secondary removeMargin onClick={() => setShowInformationModal(true)}>
                    <i className="fa fa-info">&nbsp;</i>Info
                  </Button>
                  <Button title="Submit this test" secondary removeMargin onClick={() => confirmSubmit()}>
                    <i className="fa-regular fa-paper-plane">&nbsp;</i>Submit
                  </Button>
                </SubmitAndInfoButtonContainer>
              </QuestionsBoxContainer>
            </TimerAndQuestionsContainer>
          )}
        </QuestionAndOptionsContainer>
      )}
    </TestContainer>
  );
}
